import type { Allocation, MarketModel } from '../engine/types'

/**
 * The assumptions dossier.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  READ THIS BEFORE SHIPPING F1.
 *
 *  Two separate questions are tracked for every number here, and conflating
 *  them is how a projection tool loses its credibility:
 *
 *    `kind`   — what this number IS. Permanent. A forward-looking estimate does
 *               not become a measured fact by being looked up harder.
 *    `status` — how well-founded it is RIGHT NOW. This is the one that moves.
 *
 *  The distinction matters because "verify the assumptions" is only a coherent
 *  instruction for one of the fifteen. `INFLATION_CURRENT` is a published CPI
 *  print: there is a right answer and a primary source. `EQUITY_RETURN` is a
 *  claim about the future — no citation settles it, and any source that appears
 *  to is just someone else's opinion with a URL attached. Marking the second
 *  kind 'verified' would launder a guess into a fact, which is worse than
 *  leaving it open, because it invites trust the number cannot carry.
 *
 *  So a judgment's destination is 'calibrated': the value is a choice, stated
 *  as a choice, with the sourced history it was chosen against and the reason
 *  it departs from that history. That is a stronger position than either
 *  silence or false precision, and it is the one that survives argument.
 *
 *  The constructors below make the illegal state unreachable — `status` is
 *  derived from `kind`, so a judgment cannot be marked 'verified' even by
 *  mistake. F3 renders anything still 'unverified' with a visible caveat.
 * ══════════════════════════════════════════════════════════════════════════
 */

/** What a number is. Permanent — this never changes for a given assumption. */
export type AssumptionKind =
  /** A measured, published fact. Has one right answer and a primary source. */
  | 'data'
  /** A forward-looking estimate or modelling choice. Never verifiable. */
  | 'judgment'
  /** A contested community convention. Attributable, not provable. */
  | 'norm'

/** How well-founded a number is now. This is what moves as work gets done. */
export type AssumptionStatus =
  /** Nothing behind it yet. An informed guess in the right neighbourhood. */
  | 'unverified'
  /** A judgment or norm, with its sourced basis and reasoning recorded. */
  | 'calibrated'
  /** A fact, confirmed against a primary source. Only reachable by `data`. */
  | 'verified'

export interface Assumption<T> {
  value: T
  /** Shown in the F3 panel. */
  label: string
  kind: AssumptionKind
  status: AssumptionStatus
  /**
   * For `data`: where the number is verified.
   * For `judgment`/`norm`: the sourced history the choice is measured against.
   * Read the conversion warnings — several of these are not in the units the
   * engine consumes.
   */
  basis: string
  /** The caveat, the debate, or the reason this number is contestable. */
  note: string
  /**
   * The primary source, with what it said and when it was read. Present only
   * once `status` has moved off 'unverified'.
   */
  source?: string
}

/**
 * Status is derived, never passed in. Supplying a source is what promotes an
 * assumption, and `kind` decides how far it can be promoted: `data` reaches
 * 'verified', everything else stops at 'calibrated'.
 */
function assumption<T>(
  kind: AssumptionKind,
  value: T,
  label: string,
  basis: string,
  note: string,
  source?: string,
): Assumption<T> {
  const status: AssumptionStatus =
    source === undefined ? 'unverified' : kind === 'data' ? 'verified' : 'calibrated'
  return source === undefined
    ? { value, label, kind, status, basis, note }
    : { value, label, kind, status, basis, note, source }
}

const data = <T>(value: T, label: string, basis: string, note: string, source?: string) =>
  assumption('data', value, label, basis, note, source)

const judgment = <T>(
  value: T,
  label: string,
  basis: string,
  note: string,
  source?: string,
) => assumption('judgment', value, label, basis, note, source)

const norm = <T>(value: T, label: string, basis: string, note: string, source?: string) =>
  assumption('norm', value, label, basis, note, source)

/**
 * The best status a number of this kind can ever reach. A judgment never
 * becomes a fact, so 'calibrated' is its ceiling, not a waypoint.
 */
export function bestAchievable(kind: AssumptionKind): AssumptionStatus {
  return kind === 'data' ? 'verified' : 'calibrated'
}

// ── A note on units, which is where the real bug lives ──────────────────────
//
// `mean` for the three risky sleeves is the LOG OF THE EXPECTED GROSS RETURN.
// simulate.ts sets drift = mean - vol^2/2, which gives:
//
//     E[1 + r] = exp(mean)              so   mean = ln(E[1 + r])
//     median   = exp(mean - vol^2/2)
//
// Concretely, at mean = 0.12 and vol = 0.20: the arithmetic mean simple return
// is exp(0.12) - 1 = 12.75% (vol-independent), and the median path compounds at
// exp(0.12 - 0.02) - 1 = 10.52%.
//
// To calibrate against a published CAGR — which is a geometric mean, i.e. what
// the median path should do — convert with:
//
//     mean = ln(1 + CAGR) + vol^2/2
//
// The vol^2/2 term is 2.00pp at vol = 0.20, so `mean` sits 2.00pp above
// ln(1 + CAGR) and 1.32pp above the raw CAGR figure. Dropping a CAGR straight
// into `mean` understates the 20-year median corpus by roughly 17%.
//
// ⚠ This note previously described `mean` as "the ARITHMETIC mean of the annual
// simple return", which is wrong — E[1 + r] is exp(mean), not 1 + mean. The two
// readings disagree by 2.00pp at vol = 0.20, because the conversion above
// targets the MEDIAN while the old wording implied the mean. The formula was
// always right; only the label was wrong. Anyone "fixing" the units to match the
// old label would silently make every projection ~17% more optimistic.
//
// CASH_RETURN is the exception: it is added as a plain simple return
// (`cashContribution = allocation.cash * model.cashReturn`) with no drift
// correction, so no conversion applies to it.

// ── Asset returns (annual, nominal, pre-tax) ────────────────────────────────

export const EQUITY_RETURN = judgment(
  0.12,
  'Indian equity — expected return',
  'Nifty 500 TRI realised CAGR, from the NSE Indices factsheet. `mean` here is the ' +
    'LOG OF THE EXPECTED GROSS RETURN, so a realised CAGR must be converted with ' +
    'mean = ln(1 + CAGR) + vol^2/2 before it lands here — that makes the MEDIAN path ' +
    'compound at the CAGR. No 20-year window is ' +
    'published; only QTD, YTD, 1Y, 5Y and since-inception.',
  'The single most argued-about number, and the reasoning that used to sit here was ' +
    'wrong in an interesting way. It claimed long-run Indian equity "has run higher ' +
    'than this", making 12% conservative. Nifty 500 TRI since inception is 12.13% — ' +
    'essentially 12%, not above it. So the stated justification does not hold.\n\n' +
    'The conservatism is real, but it arrives by accident: because 0.12 is consumed ' +
    'the way it is, the median path compounds at exp(0.12 - 0.20^2/2) - 1 = ' +
    '10.52%, which is 1.61pp below the realised 12.13%. That is a meaningful ' +
    'haircut nobody chose. Anyone who "fixes" the units without revisiting the value ' +
    'will make the model 1.2pp more optimistic overnight — so treat the value and ' +
    'the conversion as one decision, not two.',
  'NSE Indices Nifty 500 factsheet dated 30 June 2026, retrieved 2026-07-26 from ' +
    'nsearchives.nseindia.com/content/indices/ind_nifty_500.pdf — Total Return ' +
    'index: 12.13% since inception (base 1 Jan 1995), 12.41% over 5 years, ' +
    '-1.71% over 1 year. Price Return since inception: 10.46%.',
)

export const EQUITY_VOL = judgment(
  0.2,
  'Indian equity — volatility',
  'Standard deviation of ANNUAL Nifty 500 TRI returns. The factsheet does not ' +
    'publish this: its std-deviation row is annualised average DAILY deviation, ' +
    'computed on the PRICE index (13.98% / 14.43% / 21.98% for 1Y / 5Y / since ' +
    'inception). Different estimator, different series — not a substitute. This ' +
    'needs computing from the annual TRI series.',
  'Not a pure dispersion knob. Because drift = mean - vol^2/2, volatility also ' +
    'moves the MEDIAN: raising it from 18% to 20% drops median compounding from ' +
    '10.94% to 10.52%. So an under-stated vol flatters the projection twice over — ' +
    'a narrower fan AND a higher central path — and both errors point the same way.\n\n' +
    'Moved from 18% to 20%. The only published dispersion figures are the ' +
    "factsheet's annualised-daily-SD on the price index: 14.43% over five years, " +
    '21.98% since inception. Since inception spans ~31.5 years and is the window ' +
    'that matters for a multi-decade projection, and equity shows only mild mean ' +
    'reversion at annual horizons, the SD of annual TRI returns should land near ' +
    'that figure rather than near the five-year one. 18% sat below both and was ' +
    'chosen before any of this was looked up.\n\n' +
    '20% is still a judgment, deliberately a little below the 21.98% anchor rather ' +
    'than at it, because annualising daily deviation overstates long-horizon ' +
    'dispersion when returns mean-revert at all. Measured effect on the default ' +
    'plan: P(FIRE) 74% at 18%, 71% at 20%, 68% at 22%.\n\n' +
    'To settle this properly someone needs the annual Nifty 500 TRI series and a ' +
    'direct SD of yearly returns. niftyindices.com historical-data endpoints time ' +
    'out to automated fetches, so that is a manual download, not a lookup.',
  'NSE Indices Nifty 500 factsheet dated 30 June 2026, retrieved 2026-07-26 from ' +
    'nsearchives.nseindia.com/content/indices/ind_nifty_500.pdf — "Statistics" ' +
    'table, std. deviation 13.98% (1Y) / 14.43% (5Y) / 21.98% (since inception), ' +
    'footnoted "Average daily standard deviation annualised" and "Based on Price ' +
    'Return Index". THIS IS A DIFFERENT ESTIMATOR from the SD of annual TRI ' +
    'returns the model consumes: different frequency, and the price index rather ' +
    'than total return. It anchors the choice; it does not confirm it.',
)

export const DEBT_RETURN = judgment(
  0.07,
  'Debt — expected return',
  '10-year G-Sec yield and short-duration debt fund category returns (RBI, AMFI). ' +
    '`mean` is the log of the expected gross return — convert a category CAGR with ' +
    'mean = ln(1 + CAGR) + vol^2/2 (at vol 0.05 the gap is small, ~0.1pp, but state it).',
  'Checked in the right units and it lands almost exactly. With mean = 0.07 and ' +
    'vol = 0.05 the MEDIAN debt path compounds at exp(0.07 - 0.05^2/2) - 1 = 7.117%, ' +
    'against a 10-year G-Sec yield of 7.11% — a gap of 0.007pp.\n\n' +
    'That agreement is meaningful rather than lucky. For a bond held to maturity the ' +
    'yield IS the expected return, so a debt sleeve SHOULD compound at about the ' +
    'prevailing yield. This is the one return assumption in the dossier with a ' +
    'genuinely tight anchor — an equity index CAGR can never pin an equity ' +
    'expectation the same way, because past equity returns do not promise future ones ' +
    'and a yield does.\n\n' +
    'Taken pre-tax. Post-April-2023, debt fund gains on units bought from 1 April 2023 ' +
    'are taxed at slab rate regardless of holding period — that drag is not modelled ' +
    'until F7, so this number is optimistic for anyone in a high slab.',
  'RBI Bulletin May 2026, "State of the Economy", retrieved 2026-07-26 from ' +
    'rbidocs.rbi.org.in — 10-year G-Sec yield 7.11% as on 21 May 2026, 7.02% on ' +
    '30 April 2026, 7.04% on 30 March 2026. Yields move daily; the level is a ' +
    "reasonable long-run anchor, today's print is not a forecast.",
)

export const DEBT_VOL = judgment(
  0.05,
  'Debt — volatility',
  'Standard deviation of annual short-duration debt fund returns (AMFI category data).',
  'STILL WEAKLY ANCHORED — the least defensible number now left in the dossier.\n\n' +
    'AMFI publishes NAV history, not category return standard deviations, so this ' +
    'needs building from the series rather than looking up. Reasoning in the ' +
    'meantime: a short-duration fund runs 1-3 year duration, so a 100bp rate move ' +
    'costs roughly 1.5-3% of price, and annual return SD lands nearer 2-4% than 5%. ' +
    'On that basis 5% looks like a MEDIUM-duration sleeve, not the short one the ' +
    'basis claims.\n\n' +
    'It matters less than it looks: at a 25% debt weight, moving debt vol from 5% ' +
    'to 3% barely shifts the fan, because equity at 60% dominates portfolio ' +
    'variance. That is a reason to fix it cheaply, not a reason to leave it wrong.\n\n' +
    'A long-duration or gilt portfolio would be materially more volatile, and 2022 ' +
    'is the recent reminder.',
)

export const GOLD_RETURN = judgment(
  0.085,
  'Gold (INR) — expected return',
  'INR gold price series, long-run CAGR. `mean` is the log of the expected gross return — convert ' +
    'with mean = ln(1 + CAGR) + vol^2/2 (at vol 0.15 the gap is ~1.1pp, which is not small).',
  'INR gold returns bundle two things: the dollar gold price and rupee depreciation. ' +
    'The second has been a reliable contributor historically and may not repeat. ' +
    'Note this is a price series, not a total-return one — gold pays no income, so ' +
    'that distinction does not bite here the way it does for equity.',
)

export const GOLD_VOL = judgment(
  0.15,
  'Gold (INR) — volatility',
  'Standard deviation of annual INR gold returns.',
  'Rupee-denominated, so somewhat damped relative to dollar gold — rupee weakness ' +
    'tends to cushion INR gold when the dollar price falls.',
)

export const CASH_RETURN = judgment(
  0.05,
  'Cash / liquid — expected return',
  'Liquid fund category returns and prevailing savings rates (AMFI, RBI). Unlike the ' +
    'three risky sleeves this is added as a plain simple return with no drift ' +
    'correction, so no CAGR conversion applies.',
  'Moved from 5.5% to 5.0%, anchored on the policy corridor rather than a guess. ' +
    'The repo rate is 5.25% and the SDF 5.00%; liquid funds track the overnight ' +
    'money market inside that corridor and then lose their expense ratio, so a ' +
    'sustainable net yield sits at or just below the SDF, not above the repo. The ' +
    'old 5.5% was above the policy rate, which is only achievable by taking credit ' +
    'risk the word "liquid" is supposed to exclude.\n\n' +
    'Sanity check on the long run: at 5% long-run inflation this is a roughly zero ' +
    'real return, which is what cash has historically delivered and what it should ' +
    'be assumed to deliver. Cash is in the model to be the sleeve that does not ' +
    'grow.\n\n' +
    'Modelled as deterministic. Liquid-fund volatility at a monthly horizon is ' +
    'negligible next to the other sleeves and is not simulated.',
  'RBI monetary policy, retrieved 2026-07-26 from rbi.org.in/scripts/annualpolicy.aspx ' +
    '— policy repo rate 5.25%, held at the MPC meeting of 6-8 April 2026, stance ' +
    'neutral; Standing Deposit Facility 5.00% (set 25bp below repo by framework); ' +
    'MSF and Bank Rate 5.50%.',
)

// ── Inflation ──────────────────────────────────────────────────────────────

export const INFLATION_MEAN = judgment(
  0.05,
  'Long-run inflation — default',
  'Anchored on the statutory CPI target: 4%, tolerance band 2%–6%, set under s.45ZA ' +
    'of the RBI Act, 1934 and retained at the second five-yearly review on 25 March ' +
    '2026 for 1 Apr 2026 – 31 Mar 2031. That is what policy is committed to AIMING at, ' +
    'which anchors this number without confirming it — the target is a rule, the ' +
    'outcome is not.',
  '⚠ THE HIGHEST-LEVERAGE NUMBER IN THE DOSSIER, AND IT NEEDS AN OWNER DECISION.\n\n' +
    '6% sits at the UPPER TOLERANCE LIMIT — the edge of what policy tolerates, not ' +
    'what it aims for. Over 40 years, 4% compounds to 4.80x and 6% to 10.29x: a 2.14x ' +
    'difference in the FIRE number. On ₹8L of expenses today at 30x, that is ₹11.5 Cr ' +
    'versus ₹24.7 Cr. No other assumption here moves the answer that far.\n\n' +
    'The case FOR 6%: personal lifestyle inflation for salaried professionals — ' +
    'schooling, healthcare, housing, domestic help — has plausibly outrun headline ' +
    'CPI, and a planning tool should err toward needing more rather than less.\n\n' +
    'The case AGAINST: the current print is 4.38%, the target has now been reaffirmed ' +
    'at 4% for a further five years, and 6% is a claim that policy will miss its own ' +
    'target at the top of the band for four decades. Anchoring at the tolerance limit ' +
    'and calling it "long-run" quietly makes every result 2x more pessimistic, and ' +
    'r/FIREindia will notice.\n\n' +
    'This is a judgment about basket composition, not a measurement, which is why it ' +
    "can never be 'verified'. Because the effect is so large, it is a user-facing " +
    'slider rather than a constant — the value here is only where that slider starts, ' +
    'and the current CPI print is drawn on its track so the user can see their choice ' +
    'against the measured rate. 5% is the chosen default: above the target, below the ' +
    'ceiling, and defensible in a sentence.',
  'RBI monetary policy framework, retrieved 2026-07-26 from ' +
    'rbi.org.in/scripts/fs_overview.aspx?fn=2752 — CPI target 4%, upper tolerance 6%, ' +
    'lower tolerance 2%, determined under s.45ZA of the RBI Act, 1934. First notified ' +
    '5 August 2016; retained at the first review on 31 March 2021 and again at the ' +
    'second review on 25 March 2026, for 1 April 2026 to 31 March 2031. This anchors ' +
    'the choice; it does not confirm it — the target is what policy aims at, not what ' +
    'inflation will be.',
)

export const INFLATION_VOL = judgment(
  0.02,
  'Inflation volatility',
  'Standard deviation of annual CPI prints (MoSPI).',
  'Unconditional standard deviation of the annual rate, not the size of a single ' +
    'shock — the AR(1) scales the per-year shock down to hit this as the long-run spread.',
)

export const INFLATION_PERSISTENCE = judgment(
  0.6,
  'Inflation persistence',
  'AR(1) coefficient on annual CPI (MoSPI). A fitted coefficient would be data; 0.6 ' +
    'is a round chosen value, so it is a judgment until someone actually fits it.',
  "How much of this year's inflation carries into next year. 0 would mean each year is " +
    'independent; 1 would mean a random walk that never comes back. This being non-zero ' +
    'is why a bad inflation decade in the model clusters rather than scattering — which ' +
    'is how inflation actually hurts a retirement plan.',
)

export const INFLATION_CURRENT = data(
  0.0438,
  'Current inflation',
  'The latest MoSPI CPI print. This is the one number in the dossier with a right ' +
    'answer: a published figure, on a known release schedule, from a primary source.',
  'Seeds year one; every later year is drawn from the process above. Because it is ' +
    'genuinely verifiable it also goes stale genuinely — a CPI print is superseded ' +
    'monthly, so this needs a refresh cadence, not a one-time check.\n\n' +
    'The placeholder here was 5.5%, guessed before anything was sourced. The actual ' +
    'June 2026 print is 4.38% — the guess was 1.12pp high, which made year one of ' +
    'every path more expensive than it should have been. Note the index moved to ' +
    'base year 2024: splicing this against an older CPI series to estimate a long-run ' +
    'mean or standard deviation joins two different indices, so check the linking ' +
    'factor first.',
  'MoSPI press release, CPI for June 2026, retrieved 2026-07-26 from ' +
    'mospi.gov.in/uploads/latestReleases/latest_release_1783937698596_' +
    '1013f1a1-3400-41aa-b4f4-5bbff10db7b7_Press_Release_of_CPI_for_June_2026.pdf — ' +
    'all-India CPI YoY 4.38% (provisional, base year 2024) for June 2026 over June ' +
    '2025; May 2026 was 3.93%. All-India CFPI (food) 5.32% in June, 4.78% in May.',
)

// ── Correlations ───────────────────────────────────────────────────────────
//
// The weakest-grounded numbers in the dossier and — measured, not assumed — the
// least consequential. No primary source publishes an Indian cross-asset
// correlation matrix, so unlike the returns above there is nothing to anchor
// them to. They stay honest estimates rather than being dressed up as sourced.
//
// What makes that acceptable is the sensitivity. On the default plan at the
// balanced allocation, sweeping the entire legal range moves the headline about
// six points:
//
//     current (-0.10, 0.00, 0.10)     P(FIRE) 71%,  p10 6.69 Cr
//     all zero (independent)                  70%,  p10 6.66 Cr
//     everything correlating at ~0.4          68%,  p10 6.29 Cr
//     near-perfect ~0.9 (extreme)             65%,  p10 5.97 Cr
//
// Equity dominates portfolio variance at these weights: (0.60 x 0.20)^2 = 0.0144
// against debt's (0.25 x 0.05)^2 = 0.00016. Getting a correlation wrong by 0.2
// costs less than getting equity vol wrong by 1pp.
//
// THE THREE ARE NOT INDEPENDENT. They must form a positive semi-definite matrix,
// and combinations such as (-0.9, 0.9, 0.9) describe a world that cannot exist.
// cholesky() CLAMPS such a matrix rather than throwing, by design — so an
// impossible set does not error, it quietly simulates a LESS correlated world and
// under-states risk. Current values sit well inside the valid region (smallest
// Cholesky diagonal 0.995, i.e. no strain); assumptions.test.ts asserts it.
//
// That is also why these must never become three independent sliders: a user
// could reach an invalid state in seconds and never see an error.

export const CORR_EQUITY_DEBT = judgment(
  -0.1,
  'Equity ↔ debt correlation',
  'Rolling correlation of annual Nifty 500 TRI and debt index returns.',
  'Mildly negative, which is the entire case for holding debt alongside equity.\n\n' +
    'The mechanism matters more than the number. Bonds hedge a GROWTH shock — ' +
    'activity weakens, the central bank cuts, bond prices rise while equity falls. ' +
    'They do NOT hedge an INFLATION shock, where rates rise and both fall together. ' +
    '2022 was exactly that. So a single averaged correlation flatters ' +
    'diversification: it is mildly negative on average and turns positive in ' +
    'precisely the scenario a retirement plan most needs protection from. A single ' +
    'number cannot express a correlation that regime-switches, which is the deeper ' +
    'reason this is a judgment and not a measurement.',
)

export const CORR_EQUITY_GOLD = judgment(
  0.0,
  'Equity ↔ gold correlation',
  'Rolling correlation of annual Nifty 500 TRI and INR gold returns.',
  'Treated as uncorrelated — deliberately the unambitious choice. Gold does tend to ' +
    'rise when equity has a bad year, but not reliably enough to model as negative, ' +
    'and assuming reliable negative correlation is exactly how a portfolio model ' +
    'flatters itself into believing it is hedged when it is not. Zero claims only ' +
    'that gold does something different from equity, which is the defensible half of ' +
    'the case for holding it.',
)

export const CORR_DEBT_GOLD = judgment(
  0.1,
  'Debt ↔ gold correlation',
  'Rolling correlation of annual debt index and INR gold returns.',
  'Weakly positive — both rise when real rates fall and fall when they rise. The ' +
    'tie is genuine but loose, since gold also carries a currency leg that debt does ' +
    'not.\n\n' +
    'The least consequential number in the dossier: it links two sleeves that are ' +
    '25% and 10% of the balanced allocation, so it enters portfolio variance through ' +
    "a term two orders of magnitude below equity's. Not worth much effort to improve.",
)

// ── The FIRE rule ──────────────────────────────────────────────────────────

export const FIRE_MULTIPLE = norm(
  30,
  'FIRE target multiple',
  'Attributable, not provable. The lineage is William P. Bengen, "Determining ' +
    'Withdrawal Rates Using Historical Data", Journal of Financial Planning, October ' +
    '1994 — the origin of the 4% rule (25x) — and the Trinity study (Cooley, Hubbard ' +
    '& Walz, 1998), which tested portfolio survival rates over rolling historical ' +
    'windows. Both run on US asset series. Since 2024 there is also Indian work ' +
    '(Raju & Saraogi and successors) running the same question on Indian series, ' +
    'which is now the more relevant place to check this number.',
  '30x annual expenses, i.e. a 3.33% withdrawal rate. The American 4% rule (25x) assumes ' +
    'US inflation and a US-length retirement; Indian inflation is higher and Indian FIRE ' +
    'is often attempted earlier, so the corpus has to last longer against a faster-rising ' +
    'bill. The counter-argument used to be that 30x is not derived from Indian data at ' +
    'all. That stopped being true in 2024: Raju and Saraogi ran the analysis on Indian ' +
    'series and landed on 3.0-3.5%, which brackets our 3.33%. So the number is no ' +
    'longer a borrowed convention — but it is now answerable to that literature rather ' +
    'than to Bengen, and if those papers move, this should move with them. Shown ' +
    'openly in the UI because arguing about it is the point.\n\n' +
    'The strongest argument against 30x is not that it is too cautious in the ' +
    'abstract; it is that Bengen himself moved the other way. He raised his own ' +
    'SAFEMAX to 4.3% in 1997 and 4.5% by 2006, which is 22x, not 25x and certainly ' +
    'not 30x. The answer is horizon and tax, and both are checkable. His 4% was never ' +
    'shown to survive past 33 years and was quoted for a client of 60-65. And the ' +
    'Indian work does not rescue a longer horizon either: Raju & Saraogi test 25, 30 ' +
    'and 35 years and suggest 30 as the planning default. So someone retiring at 45 ' +
    'here, planning about 45 years, is outside the window BOTH literatures have ever ' +
    'tested — which is the honest reason to sit at the cautious end of the Indian ' +
    "range rather than anywhere near Bengen's revised number.\n\n" +
    'What the norm cannot see: Bengen and Trinity both assume a ~30-year retirement, ' +
    'and neither models the tax a retiree actually pays on the way out. Trinity ' +
    'says so in terms — it states it does not adjust for taxes or transaction ' +
    'costs. Bengen is not quoted here on tax either way, so read that half as an ' +
    'absence of any tax model rather than as a sourced statement about his. ' +
    'Someone retiring at 45 in India is planning for closer to 45 years, and pays ' +
    'Indian capital gains on the way out. ' +
    'The first pushes the required multiple UP, and F7a now prices the second. ' +
    'Until it landed, every projection here was pre-tax at the point of drawdown, ' +
    'and 30x was doing double duty as both a withdrawal rule and an unstated tax ' +
    'buffer. That second job is now done explicitly: the engine is handed 30x ' +
    'multiplied by a withdrawal-tax gross-up, so this number is the pre-tax norm ' +
    'and nothing else.\n\n' +
    'Be careful what that does NOT settle, because this is the weakest joint in ' +
    'the dossier. The buffer was never quantified — no figure was ever attached to ' +
    'it — so swapping an unstated buffer for a computed gross-up cannot be checked ' +
    'the way every other number here can. If 30x sat at the cautious end of the ' +
    'Indian range PARTLY because tax went unpriced, then pricing tax on top of it ' +
    'charges for the same risk twice, and the honest position is that the size of ' +
    'that double count is unknown. The value is therefore left at 30x rather than ' +
    'shaved toward 25x, which is the conservative direction to be wrong in.\n\n' +
    'One consequence worth stating plainly, because it cuts against us: the Indian ' +
    'research puts a 30%-bracket retiree at 2.7-3.2%, which is 31-37x. The gross-up ' +
    'moves a typical plan to roughly 32x, which lands inside that range rather than ' +
    'below it. But it is computed for a MODEL portfolio at a model holding period, ' +
    'not for whatever you actually hold or paid, and it deliberately does not vary ' +
    'with the risk mix you picked — see the withdrawal-tax rows below.\n\n' +
    'This is the single number the UI states most bluntly — the slider hint quotes ' +
    'the tax-adjusted multiple with 30x named beside it — so it is named explicitly ' +
    'in the footer disclaimer rather than being covered by a general caveat about ' +
    'returns and inflation.',
  'Bengen, W. P., "Determining Withdrawal Rates Using Historical Data", Journal of ' +
    "Financial Planning 7(4): 171-180, October 1994 — read 2026-08-02 in the FPA's " +
    'March 2004 "Best of 25 Years" reprint at financialplanningassociation.org. The ' +
    '4% is a FIRST-YEAR withdrawal, inflation-adjusted thereafter, from a 50/50 ' +
    'portfolio, and its stated guarantee is that no historical case exhausted the ' +
    'portfolio before 33 YEARS — 4.25% could go in 28. His client is aged 60-65.\n\n' +
    'Cooley, Hubbard & Walz, "Retirement Savings: Choosing a Withdrawal Rate That Is ' +
    'Sustainable", AAII Journal, February 1998 — read 2026-08-02 at ' +
    'aaii.com/journal/199802/feature.pdf. S&P 500 and long-term HIGH-GRADE CORPORATE ' +
    "bonds (not Bengen's Treasuries), Ibbotson data 1926-1995, payout periods of " +
    '15/20/25/30 years. It states explicitly that it does NOT adjust for taxes or ' +
    'transaction costs.\n\n' +
    'Bengen has since revised his own figure UP: 4.3% in 1997 (JFP 10(6): 84-97, ' +
    'adding 30% small-caps) and 4.5% in his 2006 book. Duquette reaches 4.7% with ' +
    'mid-caps (FPA Journal, November 2023, read 2026-08-02).\n\n' +
    'India, and this is the part that changed: Raju, R. & Saraogi, R., "Balancing ' +
    'Acts: Safe Withdrawal Rates in the Indian Context", SSRN 4697720, doi ' +
    '10.2139/ssrn.4697720, January 2024 — READ IN FULL 2026-08-02. Its abstract ' +
    'states that the 4% rule "is not appropriate in India\'s context; rather a range ' +
    'between 3.0% and 3.5% is more appropriate", and that failure risk climbs sharply ' +
    'above 3.75%. Method: Indian data from January 2000 to August 2023 (284 ' +
    'month-year combinations), circular block bootstrap plus randomised Monte Carlo, ' +
    'following Raju & Raju (2023) for total-return equity and short-term fixed ' +
    'income. ALL RETURNS ARE REAL, so the withdrawal holds its purchasing power and ' +
    "the rate is directly comparable to Bengen's. Portfolios run from 100% fixed " +
    'deposit to 40:60 FD:equity, annually rebalanced. Tax is modelled — tax on fixed ' +
    'deposit interest is called out as a significant drag. Retirement durations ' +
    'tested: 25, 30 and 35 years; the practitioner framework suggests a 30-year ' +
    'horizon at a minimum 90% success rate. One figure worth remembering: an ' +
    'all-deposit portfolio withdrawing over 3.5% fails 100% of the time over 35 ' +
    'years.\n\n' +
    'A later Raju paper (SSRN 5114252, data 1992-2024) reportedly models tax by ' +
    'bracket and finds ~3.5-4.2% below the 12 lakh threshold and ~2.7-3.2% at 30%. ' +
    'STILL CORROBORATED, NOT READ — SSRN blocks automated access and that one has ' +
    'not been retrieved by hand, so those two ranges are secondary reporting. The ' +
    '3.0-3.5% above is not: it is quoted from the paper.',
)

// ── Withdrawal-phase tax (F7a) ──────────────────────────────────────────────
//
// These are the only entries in this file with ONE RIGHT ANSWER. Every number
// above is a judgment or a norm; a statutory rate is a fact, so these are the
// second and subsequent `data` entries the dossier has ever carried, and the
// only ones besides INFLATION_CURRENT that can legitimately read 'verified'.
//
// Three things to know before changing any of them.
//
// FY 2026-27 runs under the INCOME-TAX ACT, 2025, not the 1961 Act. The
// capital-gains charging sections are s.196 (short-term on STT-paid equity),
// s.197 (long-term, general) and s.198 (long-term on STT-paid equity) — the
// successors to s.111A, s.112 and s.112A. Anything written before 1 April 2026
// cites the old numbers, and because the VALUES did not change, a stale source
// quotes the right figure under the wrong section and still reads as current.
//
// The statute site 403s to every automated fetch and its headline tables were
// still serving the PREVIOUS year when these were read. The Budget 2026
// documents are the route. `.claude/ledger/capital-gains-fy2026-27.md` carries
// the full provenance and the caveat on how the rates were read.
//
// 23 July 2024 split a single year into two regimes. These are the post-split
// values; anything historical spanning that date needs both.

export const EQUITY_LTCG_RATE = data(
  0.125,
  'Equity LTCG rate',
  'Section 198 of the Income-tax Act, 2025 — long-term capital gains on STT-paid ' +
    'listed equity and units of an equity-oriented fund. Successor to s.112A of the ' +
    '1961 Act. Applies to gains above the annual exemption below.',
  'Long-term here means held more than 12 months (s.101). The rate is conditional ' +
    'on securities transaction tax having been paid on the transfer, which is true ' +
    'for ordinary mutual-fund redemptions and listed-equity sales. Note the basic ' +
    'exemption limit may NOT be set off against gains charged at this concessional ' +
    'rate — a retiree with no other income still pays it on everything above ' +
    '₹1,25,000.',
  'Finance Bill 2026 (Bill No. 3 of 2026), First Schedule, retrieved 2026-09-12 ' +
    'from indiabudget.gov.in/doc/Finance_Bill.pdf — states "long-term capital gains ' +
    'referred to in section 198 of the said Act exceeding ₹ 125000 — 12.5%". The ' +
    'Memorandum (indiabudget.gov.in/doc/memo.pdf) confirms no change to tax rates ' +
    'for tax year 2026-27. See .claude/ledger/capital-gains-fy2026-27.md.',
)

export const EQUITY_LTCG_EXEMPTION = data(
  125_000,
  'Equity LTCG exemption',
  'The annual exemption on the aggregate of section 198 long-term gains. A rupee ' +
    'threshold fixed in the statute, not a percentage and not indexed to inflation.',
  'This is the single most consequential number in the withdrawal-tax model for a ' +
    'modest plan, and the reason the tax drag is near-invisible for someone ' +
    'retiring soon. It is NOMINAL: it does not rise with prices, so its protective ' +
    'value decays over a long accumulation. Someone retiring in 35 years on an ' +
    'inflated withdrawal sees it absorb almost nothing, which is most of why the ' +
    'gross-up grows with horizon rather than staying flat.',
  'Finance Bill 2026 (Bill No. 3 of 2026), First Schedule, retrieved 2026-09-12 ' +
    'from indiabudget.gov.in/doc/Finance_Bill.pdf — "exceeding ₹ 125000" beside the ' +
    'section 198 rate. Raised from ₹1,00,000 with effect from 23 July 2024. See ' +
    '.claude/ledger/capital-gains-fy2026-27.md.',
)

export const OTHER_LTCG_RATE = data(
  0.125,
  'Other-asset LTCG rate',
  'Section 197 of the Income-tax Act, 2025 — long-term capital gains on assets ' +
    'outside section 198. Successor to s.112 of the 1961 Act. This is the rate the ' +
    'gold sleeve pays.',
  'No annual exemption attaches to this one, which is why the gold sleeve starts ' +
    'paying from the first rupee of gain while equity does not. Long-term is more ' +
    'than 12 months for a listed instrument such as a gold ETF and more than 24 ' +
    'months for physical gold (s.101); the model assumes the listed case, which is ' +
    'the shorter threshold and so the more favourable of the two.',
  'Finance Bill 2026 (Bill No. 3 of 2026), First Schedule, retrieved 2026-09-12 ' +
    'from indiabudget.gov.in/doc/Finance_Bill.pdf — 12.5% for long-term capital ' +
    'gains referred to in section 197(4) and for other long-term capital gains. ' +
    'See .claude/ledger/capital-gains-fy2026-27.md.',
)

export const CESS_RATE = data(
  0.04,
  'Health and education cess',
  'Four per cent of (income-tax + surcharge), charged in all cases with no ' +
    'marginal relief on the cess itself. Finance Bill 2026 s.3(15) and s.3(16).',
  'Applies on top of every other rate here, so it is the last multiplication in ' +
    'the order of operations: slab or flat rate, then surcharge, then marginal ' +
    'relief, then cess. Small but not negligible — worth about 0.3 percentage ' +
    'points of the withdrawal gross-up on a typical plan, and it was missed in the ' +
    'first draft of this model.',
  'Already verified for this repo in .claude/ledger/income-tax-slabs-fy2026-27.md ' +
    '(Finance Bill 2026 s.3(15) and s.3(16), retrieved 2026-08-05), and re-read in ' +
    'the same document on 2026-09-12 while sourcing the capital-gains rates.',
)

export const SURCHARGE_GAINS_CAP = data(
  0.15,
  'Surcharge cap on gains',
  'Surcharge on the income-tax computed on the dividend and capital-gains ' +
    'component — sections 196, 197 and 198 — shall not exceed 15%, however high ' +
    'total income goes. Memorandum to the Finance Bill 2026, paragraph 4.1.',
  'Surcharge itself starts above ₹50 lakh of total income (10%), rises to 15% ' +
    'above ₹1 crore and to 25% above ₹2 crore — but the gains component is capped ' +
    'here at 15%. That only binds on large withdrawals: a retiree drawing under ' +
    '₹50 lakh of realised gain pays no surcharge at all. Marginal relief at each ' +
    'threshold is deliberately not modelled; omitting it slightly OVERSTATES tax ' +
    'in a narrow band just above each step.',
  'Memorandum to the Finance Bill 2026, paragraph 4.1, retrieved 2026-09-12 from ' +
    'indiabudget.gov.in/doc/memo.pdf — "the rate of surcharge on the amount of ' +
    'income-tax computed in respect of that part of income shall not exceed 15%". ' +
    'Bands in .claude/ledger/income-tax-slabs-fy2026-27.md.',
)

/**
 * The s.202 default-regime slab schedule, and the capped surcharge schedule.
 *
 * NOT `Assumption<number>` entries, for a reason worth stating: the dossier's
 * contract is one scalar per row, rendered as a single figure in the F3 panel,
 * and a seven-band table is not that shape. Both schedules are already VERIFIED
 * in `.claude/ledger/income-tax-slabs-fy2026-27.md` and are reused here rather
 * than re-sourced — the proposal's "slab rates are verified there and reusable".
 *
 * `upTo` is the top of each band, inclusive. The debt sleeve needs these because
 * a unit of a specified mutual fund acquired on or after 1 April 2023 is deemed
 * SHORT-TERM regardless of holding period (s.76 of the Income-tax Act, 2025,
 * successor to s.50AA), so its gain is taxed at slab rates rather than at the
 * 12.5% long-term rate. That single rule is why a debt-heavy retiree is the
 * expensive case — see `.claude/ledger/capital-gains-specified-mutual-fund-fy2026-27.md`.
 */
export interface TaxBand {
  readonly upTo: number
  readonly rate: number
}

export const SLAB_BANDS: readonly TaxBand[] = [
  { upTo: 400_000, rate: 0 },
  { upTo: 800_000, rate: 0.05 },
  { upTo: 1_200_000, rate: 0.1 },
  { upTo: 1_600_000, rate: 0.15 },
  { upTo: 2_000_000, rate: 0.2 },
  { upTo: 2_400_000, rate: 0.25 },
  { upTo: Number.POSITIVE_INFINITY, rate: 0.3 },
]

/**
 * Surcharge on the gains component, already capped at SURCHARGE_GAINS_CAP. The
 * 25% and 37% bands that apply to other income never appear here because the cap
 * binds first.
 */
export const GAINS_SURCHARGE_BANDS: readonly TaxBand[] = [
  { upTo: 5_000_000, rate: 0 },
  { upTo: 10_000_000, rate: 0.1 },
  // Reads the assumption rather than repeating 0.15. The review found these had
  // drifted apart by construction: the panel displayed SURCHARGE_GAINS_CAP as a
  // sourced fact while the model read an independent literal, so editing the
  // sourced row changed the screen and changed no arithmetic.
  { upTo: Number.POSITIVE_INFINITY, rate: SURCHARGE_GAINS_CAP.value },
]


// ── Composite model handed to the engine ───────────────────────────────────

export const INDIA_MARKET_MODEL: MarketModel = {
  assets: {
    equity: { mean: EQUITY_RETURN.value, vol: EQUITY_VOL.value },
    debt: { mean: DEBT_RETURN.value, vol: DEBT_VOL.value },
    gold: { mean: GOLD_RETURN.value, vol: GOLD_VOL.value },
  },
  cashReturn: CASH_RETURN.value,
  // Order must match RISKY_ASSETS: equity, debt, gold.
  correlation: [
    [1, CORR_EQUITY_DEBT.value, CORR_EQUITY_GOLD.value],
    [CORR_EQUITY_DEBT.value, 1, CORR_DEBT_GOLD.value],
    [CORR_EQUITY_GOLD.value, CORR_DEBT_GOLD.value, 1],
  ],
  inflation: {
    mean: INFLATION_MEAN.value,
    vol: INFLATION_VOL.value,
    phi: INFLATION_PERSISTENCE.value,
    initial: INFLATION_CURRENT.value,
  },
}

/** Everything F3 needs to render the panel, in display order. */
export const ASSUMPTION_DOSSIER: Assumption<number>[] = [
  EQUITY_RETURN,
  EQUITY_VOL,
  DEBT_RETURN,
  DEBT_VOL,
  GOLD_RETURN,
  GOLD_VOL,
  CASH_RETURN,
  INFLATION_MEAN,
  INFLATION_VOL,
  INFLATION_PERSISTENCE,
  INFLATION_CURRENT,
  CORR_EQUITY_DEBT,
  CORR_EQUITY_GOLD,
  CORR_DEBT_GOLD,
  FIRE_MULTIPLE,
  // The withdrawal-tax block. Every one of these is `data` — the only rows in
  // the panel that can read 'verified', and the only ones where a reader who
  // disagrees is simply wrong rather than holding a different opinion.
  EQUITY_LTCG_RATE,
  EQUITY_LTCG_EXEMPTION,
  OTHER_LTCG_RATE,
  CESS_RATE,
  SURCHARGE_GAINS_CAP,
]

/**
 * True once every number is as well-founded as its kind allows — facts verified,
 * judgments and norms calibrated.
 *
 * This replaces ALL_ASSUMPTIONS_VERIFIED, which asked whether all fifteen were
 * 'verified' and so could never have been true: fourteen of them are not the
 * kind of thing that gets verified. A flag that can only ever read false is not
 * a check, it is a decoration.
 */
export const ALL_ASSUMPTIONS_SOURCED = ASSUMPTION_DOSSIER.every(
  (a) => a.status === bestAchievable(a.kind),
)

/** What is still outstanding, for the F1 pre-launch checklist and the F3 panel. */
export const ASSUMPTIONS_OUTSTANDING = ASSUMPTION_DOSSIER.filter(
  (a) => a.status !== bestAchievable(a.kind),
)

// ── Risk profiles ──────────────────────────────────────────────────────────

export interface RiskProfile {
  id: string
  label: string
  blurb: string
  allocation: Allocation
}

/**
 * These allocations are judgments too, and they are not wrapped in Assumption<T>
 * because they are presets rather than parameters. Worth revisiting if F3 starts
 * showing provenance for them.
 */
export const RISK_PROFILES: RiskProfile[] = [
  {
    id: 'conservative',
    label: 'Conservative',
    blurb: 'Sleeps well. Gets there later, and less often.',
    allocation: { equity: 0.3, debt: 0.5, gold: 0.1, cash: 0.1 },
  },
  {
    id: 'balanced',
    label: 'Balanced',
    blurb: 'The default most advisors land on.',
    allocation: { equity: 0.6, debt: 0.25, gold: 0.1, cash: 0.05 },
  },
  {
    id: 'aggressive',
    label: 'Aggressive',
    blurb: 'Widest range of outcomes. Best median, worst tail.',
    allocation: { equity: 0.8, debt: 0.1, gold: 0.08, cash: 0.02 },
  },
]

export const DEFAULT_RISK_PROFILE = RISK_PROFILES[1]
